import * as THREE from 'three';

export interface NavTriangle {
    readonly centroid: THREE.Vector3;
}

export class NavMesh {
    public readonly triangles: NavTriangle[] = [];
    private readonly adjacency: number[][] = [];

    constructor(mesh: THREE.Mesh) {
        mesh.updateWorldMatrix(true, false);
        const vertices = this.parseGeometry(mesh.geometry as THREE.BufferGeometry, mesh.matrixWorld);
        this.buildAdjacency(vertices);
    }

    private parseGeometry(geo: THREE.BufferGeometry, matrix: THREE.Matrix4): THREE.Vector3[][] {
        const posAttr = geo.attributes.position as THREE.BufferAttribute;
        const index = geo.index;
        const count = index ? index.count : posAttr.count;
        const triCount = Math.floor(count / 3);
        const vertices: THREE.Vector3[][] = [];

        for (let i = 0; i < triCount; i++) {
            const ia = index ? index.getX(i * 3)     : i * 3;
            const ib = index ? index.getX(i * 3 + 1) : i * 3 + 1;
            const ic = index ? index.getX(i * 3 + 2) : i * 3 + 2;

            const vA = new THREE.Vector3().fromBufferAttribute(posAttr, ia).applyMatrix4(matrix);
            const vB = new THREE.Vector3().fromBufferAttribute(posAttr, ib).applyMatrix4(matrix);
            const vC = new THREE.Vector3().fromBufferAttribute(posAttr, ic).applyMatrix4(matrix);

            this.triangles.push({
                centroid: new THREE.Vector3().addVectors(vA, vB).add(vC).divideScalar(3),
            });
            vertices.push([vA, vB, vC]);
        }
        return vertices;
    }

    // Detects shared edges by quantising world-space vertex positions rather than
    // comparing raw vertex indices. This correctly handles non-indexed and
    // split-vertex GLTF geometry exported from Blender, where adjacent triangles
    // share the same geometric position but have different buffer indices.
    private buildAdjacency(vertices: THREE.Vector3[][]): void {
        const n = this.triangles.length;
        for (let i = 0; i < n; i++) this.adjacency.push([]);

        const quantize = (v: THREE.Vector3): string =>
            `${Math.round(v.x * 1000)},${Math.round(v.y * 1000)},${Math.round(v.z * 1000)}`;

        const edgeMap = new Map<string, number[]>();
        for (let t = 0; t < n; t++) {
            const [vA, vB, vC] = vertices[t];
            for (const [p, q] of [[vA, vB], [vB, vC], [vC, vA]] as [THREE.Vector3, THREE.Vector3][]) {
                const kp = quantize(p);
                const kq = quantize(q);
                const key = kp < kq ? `${kp}|${kq}` : `${kq}|${kp}`;
                if (!edgeMap.has(key)) edgeMap.set(key, []);
                edgeMap.get(key).push(t);
            }
        }

        for (const [, tris] of edgeMap) {
            if (tris.length === 2) {
                this.adjacency[tris[0]].push(tris[1]);
                this.adjacency[tris[1]].push(tris[0]);
            }
        }
    }

    // Uses XZ-only distance so that characters standing at a different elevation
    // than the nav mesh (e.g. atop a capsule collider) still resolve to the correct
    // triangle beneath them rather than a geometrically closer but horizontally
    // distant one.
    public findNearestTriangle(point: THREE.Vector3): number {
        let nearest = 0;
        let minDist = Infinity;
        for (let i = 0; i < this.triangles.length; i++) {
            const c = this.triangles[i].centroid;
            const dx = c.x - point.x;
            const dz = c.z - point.z;
            const d = dx * dx + dz * dz;
            if (d < minDist) { minDist = d; nearest = i; }
        }
        return nearest;
    }

    public findPath(fromIdx: number, toIdx: number): number[] {
        if (fromIdx === toIdx || this.triangles.length === 0) return [fromIdx];

        const openSet = new Set<number>([fromIdx]);
        const cameFrom = new Map<number, number>();
        const g = new Map<number, number>([[fromIdx, 0]]);
        const f = new Map<number, number>([[fromIdx, this.centroidDist(fromIdx, toIdx)]]);

        while (openSet.size > 0) {
            let cur = -1;
            let best = Infinity;
            for (const n of openSet) {
                const fv = f.get(n) ?? Infinity;
                if (fv < best) { best = fv; cur = n; }
            }

            if (cur === toIdx) return this.reconstructPath(cameFrom, cur);
            openSet.delete(cur);

            for (const nb of this.adjacency[cur]) {
                const tg = (g.get(cur) ?? Infinity) + this.centroidDist(cur, nb);
                if (tg < (g.get(nb) ?? Infinity)) {
                    cameFrom.set(nb, cur);
                    g.set(nb, tg);
                    f.set(nb, tg + this.centroidDist(nb, toIdx));
                    openSet.add(nb);
                }
            }
        }

        return [fromIdx];
    }

    public getRandomTriangle(): number {
        return Math.floor(Math.random() * this.triangles.length);
    }

    public getRandomSpawnPosition(): THREE.Vector3 {
        return this.triangles[this.getRandomTriangle()].centroid.clone();
    }

    private centroidDist(a: number, b: number): number {
        return this.triangles[a].centroid.distanceTo(this.triangles[b].centroid);
    }

    private reconstructPath(cameFrom: Map<number, number>, current: number): number[] {
        const path = [current];
        while (cameFrom.has(current)) {
            current = cameFrom.get(current);
            path.unshift(current);
        }
        return path;
    }
}
